import {
  Box,
  HStack,
  IconButton,
  Progress, Slider, SliderFilledTrack, SliderThumb,
  SliderTrack,
} from "@chakra-ui/react";
import React, {ForwardedRef, forwardRef, MutableRefObject, useEffect,
                useImperativeHandle, useRef, useState} from "react";
import {IoPause,IoPlay,IoVolumeMedium} from "react-icons/io5";

import {useTheme} from "../hooks/ThemeProvider";
import {formatTime} from "../Utils";
import Markdown from "./Markdown";

export type AudioPlayerUpdate = {
  updateSource: (sourceUrl: string) => void;
  stopPlayer: () => void;
  startPlayer: () => void;
}

type AudioProps = {
  sourceUrl : string;
  timesPlayed?: MutableRefObject<number>;
  onAudioPlayed?: (timesPlayed: number) => void;
  onAudioEnded?: () => void;
  maxNumberOfReproductions?: number;
  noOutline?: boolean;

  hidePlayButton?: boolean;
  autoPlay?: boolean;
  hidden?:boolean;
};

const AudioPlayer = forwardRef((props: AudioProps, ref: ForwardedRef<AudioPlayerUpdate>) => {
  const theme = useTheme().theme;
  const maxNumberOfReproductions = props.maxNumberOfReproductions!==undefined?props.maxNumberOfReproductions:2;
  const infinitePlays = maxNumberOfReproductions===0;

  const [playable, setPlayable] = useState<boolean>(
    ( (props.sourceUrl!==undefined && props.sourceUrl.trim().length > 0)
     && (infinitePlays || (props.timesPlayed !== undefined && props.timesPlayed.current < maxNumberOfReproductions)))
  );
  const initialVolume = Number(localStorage.getItem('PeleX_Volume') || '1');
  const [progressValue, setProgressValue] = useState<number>(0);
  const [formattedTime, setFormattedTime] = useState<string>("00:00");

  const audioPlayer = useRef<HTMLAudioElement>(null);
  const audioSource = useRef<HTMLSourceElement>(null);

  const playIcon = <IoPlay />;
  const pauseIcon = <IoPause />;
  const [audioIcon, setAudioIcon] = useState<JSX.Element>(playIcon);

  const playIf = async () => {
    if (props.timesPlayed && playable) {
      if (infinitePlays || (props.timesPlayed.current < maxNumberOfReproductions)) {
        props.timesPlayed.current = props.timesPlayed.current + 1;
        setPlayable(false);
        // save timesPlayed to the DB
        if (props.onAudioPlayed) props.onAudioPlayed(props.timesPlayed.current);
        await audioPlayer?.current?.play().catch(() => { });
      }
    } else
      if (audioPlayer?.current && !audioPlayer?.current?.paused) {
        setAudioIcon(playIcon);
        audioPlayer?.current?.pause();
      }
      else {
        setAudioIcon(pauseIcon);
        audioPlayer?.current?.play().catch(() => { });
      }
  };

  const updateProgress = () =>{
    if (audioPlayer && audioPlayer.current) {
      setProgressValue(audioPlayer?.current?.currentTime / audioPlayer?.current?.duration * 100);
      setFormattedTime(formatTime(audioPlayer?.current?.currentTime || 0));
    }
  }

  const changeVolume = (volume:number) =>{
    if (audioPlayer && audioPlayer.current) {
      audioPlayer.current.volume = (volume) / 100;
      localStorage.setItem('PeleX_Volume', audioPlayer.current.volume.toString());
    }
  }
  useImperativeHandle(ref, () => ({
    updateSource(blobUrl:string) {
      if (audioPlayer.current && audioSource.current) {
        audioSource.current.src = blobUrl;
        audioPlayer.current.load();
        setPlayable(blobUrl !=="" );
      }
    },
    stopPlayer() {
      if (audioPlayer?.current && !audioPlayer?.current?.paused) {
        setAudioIcon(playIcon);
        audioPlayer?.current?.pause();
      }
    },
    startPlayer() {
      if (audioPlayer?.current && audioPlayer?.current?.paused) {
        setAudioIcon(pauseIcon);
        audioPlayer?.current?.play().catch(() => { });
      }
    }
  }));

  /*
  useEffect(() => {
    console.log(!playable || audioPlayer?.current?.paused===undefined
      || audioPlayer?.current?.paused);
    if (!playable || audioPlayer?.current?.paused===undefined
      || audioPlayer?.current?.paused)
      setAudioIcon(playIcon);
    else
      setAudioIcon(pauseIcon);
  }, [playable, audioPlayer?.current?.paused])
  */

  const refresh = ()=>{
    if (!props.timesPlayed && props.autoPlay===undefined)
      setPlayable(true);
    setProgressValue(0);
    setFormattedTime("00:00");
    if (audioPlayer && audioPlayer.current) {
      audioPlayer.current.currentTime = 0;
    }
  }

  useEffect(() => {
    if (props.autoPlay) {
      setPlayable(false);
      playIf();
    }
  }, [props.autoPlay]);

  useEffect(() => {
    if (audioPlayer && audioPlayer.current)
        audioPlayer.current.volume = initialVolume;
  }, []);

  if (props.hidden) {
    return (
        <>
        <audio ref={audioPlayer}
               controls={false}
               onTimeUpdate={()=> {updateProgress()}}
               onEnded={()=> {setPlayable(infinitePlays ||
                 (props.timesPlayed !== undefined && props.timesPlayed.current < maxNumberOfReproductions));
                 setAudioIcon(playIcon);
                 if (props.onAudioEnded) props.onAudioEnded();
                 } }
               onLoadedData={()=>refresh()}
        >
          <source type="audio/mpeg"
                  ref={audioSource}
                  src={props.sourceUrl}
          />
        </audio>
        </>
        );
  } else return (
    <Box>
      <HStack
        { ...(props.noOutline !== true && {
        marginTop:"2",
        marginBottom:"2",
        padding:"2",
        borderWidth:"1px",
        borderRadius:"lg",
        borderColor:theme.OUTLINE,
        borderStyle:"solid",
        align:"center",
        spacing:'10px',
        width:"fit-content"
        } ) }
      >
        {/*
        the default audio controls are hidden
        custom controls are placed to have better control on the configuration
         for example to prevent seeking the audio
        */}
        {!props.hidePlayButton &&
          <IconButton
            marginInline = {0}
            aria-label="Play Audio"
            onClick={()=>  { return playIf(); }}
            disabled={!playable}
            /*icon={ !playable || audioPlayer?.current?.paused===undefined
              || audioPlayer?.current?.paused ?
              playIcon : pauseIcon }*/
            icon={ audioIcon }
            size="lg"
            colorScheme={theme.COLOR_SCHEME}
          />
        }
        <Progress  max={100}
                    backgroundColor={theme.LIGHT_BG}
                   colorScheme={theme.COLOR_SCHEME}
                   value={progressValue} size="sm" width={100}/>
        <Markdown
          content={formattedTime}
        />
        <IoVolumeMedium />
        <Slider aria-label='slider-ex-1'
                size="sm"
                defaultValue={initialVolume*100}
                onChange={(val) => changeVolume(val)}
                width={100}
                colorScheme={theme.COLOR_SCHEME}>
          <SliderTrack>
            <SliderFilledTrack />
          </SliderTrack>
          <SliderThumb boxSize={3} bg={theme.TEXT_PRIMARY} />
        </Slider>
      </HStack>

        <audio ref={audioPlayer}
               controls={false}
               onTimeUpdate={()=> {updateProgress()}}
               onEnded={()=> {setPlayable(infinitePlays ||
                 (props.timesPlayed !== undefined && props.timesPlayed.current < maxNumberOfReproductions));
                 setAudioIcon(playIcon);
                 if (props.onAudioEnded) props.onAudioEnded();
                 } }
               onLoadedData={()=>refresh()}
        >
          <source type="audio/mpeg"
                  ref={audioSource}
                  src={props.sourceUrl}
          />
        </audio>
    </Box>
  );
}
);

export default AudioPlayer;
